"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setUserDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          <div className="flex items-center">
            <Link href="/" className="flex items-center">
              <Image
                src="/images/logo.webp"
                alt="TopCat Helicopters"
                width={160}
                height={38}
                className="h-8 sm:h-10 lg:h-12 w-auto"
                priority
              />
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <nav className="hidden sm:flex space-x-4 lg:space-x-8 items-center">
              <Link
                href="/private-pilot-license"
                className="px-4 py-2 border-2 border-accent text-accent font-medium rounded-full transition-all duration-300 hover:bg-accent hover:!text-white"
              >
                PPL(H)
              </Link>
              <Link
                href="/#services"
                className="text-sm lg:text-base text-gray-700 hover:text-accent transition-colors"
              >
                Services
              </Link>
              <Link
                href="/#about"
                className="text-sm lg:text-base text-gray-700 hover:text-accent transition-colors"
              >
                About
              </Link>
              <Link
                href="/#team"
                className="text-sm lg:text-base text-gray-700 hover:text-accent transition-colors"
              >
                Team
              </Link>
              <Link
                href="/#contact"
                className="text-sm lg:text-base text-gray-700 hover:text-accent transition-colors"
              >
                Contact
              </Link>
            </nav>

            {/* User dropdown - only show when logged in */}
            {user && (
              <div className="relative hidden sm:block" ref={dropdownRef}>
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-accent hover:bg-gray-50 transition-colors border border-gray-300 hover:border-accent"
                >
                  <span>Hello, {user.name}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      userDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown menu */}
                {userDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <Link
                      href="/dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-accent transition-colors"
                      onClick={() => setUserDropdownOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setUserDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-accent transition-colors"
                    >
                      Uitloggen
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="sm:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? (
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu overlay - only when menu is open */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 top-14 sm:top-16 bg-transparent z-30 sm:hidden"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
        )}

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="relative sm:hidden bg-white border-t border-gray-200 z-40">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                href="/private-pilot-license"
                className="mx-3 my-2 px-4 py-2 border-2 border-accent text-accent font-medium rounded-full transition-all duration-300 text-center block hover:bg-accent hover:!text-white"
                onClick={() => setMobileMenuOpen(false)}
              >
                PPL(H)
              </Link>
              <Link
                href="/#services"
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-accent hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </Link>
              <Link
                href="/#about"
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-accent hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link
                href="/#team"
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-accent hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Team
              </Link>
              <Link
                href="/#contact"
                className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-accent hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>

              {/* Mobile user menu - only show when logged in */}
              {user && (
                <>
                  <div className="border-t border-gray-200 my-2"></div>
                  <div className="px-3 py-2">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-base font-medium text-gray-900">
                        Howdy, {user.name}
                      </span>
                    </div>
                  </div>
                  <Link
                    href="/dashboard"
                    className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-accent hover:bg-gray-50 rounded-md transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      logout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:text-accent hover:bg-gray-50 rounded-md transition-colors"
                  >
                    Uitloggen
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
